import { apiClient } from '@indipia/shared/api/client';
import { API_PRX as SHARED_API_PRX } from '@indipia/shared/api/endpoints';
import { API_PRX } from './endpoints';

/** 토스 공식 권고(docs.tosspayments.com/resources/glossary/timeout, "결제 처리 API의 Read Timeout은
 *  60초로 설정") 반영(2026-09-07) — apiClient 공통 기본 타임아웃(10초, packages/shared)은 다른 모든
 *  API 호출에도 쓰이는 값이라 여기서 못 바꾸고, 실제 토스 PG를 호출하는 요청(카드 등록·업그레이드
 *  결제·재시도 결제·환불)에만 개별 요청 옵션으로 더 긴 타임아웃을 준다. ExLink(60s)·sv-billing→ExLink
 *  read timeout(65s)보다 넉넉하게 70초로 설정. */
const PG_CALL_TIMEOUT_MS = 70000;

export type TierCode = 'FREE' | 'STARTER' | 'PRO' | 'ALLIN';
export type BillingCycle = 'monthly' | 'annual';

export interface PricingTier {
  tierCode: TierCode;
  monthlyPrice: number;
  annualMonthlyDisplay: number;
  annualTotal: number;
  /** 원본 NULL(무제한)도 API가 라벨 문자열로 변환해 응답 — FE null 분기 금지 */
  maxConcurrentSiteLabel: string;
  /** 추천 배지 노출 대상 여부 — BE `TM_SERVICE_PRICE.FEATURED_YN` 운영 컬럼(2026-09-09 신설, DDL
   *  실행 전까지는 항상 false로 응답). PRO 기본 fallback은 컬럼 신설 전 레거시 동작 보존용. */
  featuredYn?: boolean;
}

export interface PromotionInfo {
  promotionCode: string;
  promotionType: string;
  discountRateAnnual: number;
  discountRateMonthly: number;
  validUntilDtm: string;
}

export interface PricingPlansResponse {
  tiers: PricingTier[];
  activePromotions: PromotionInfo[];
}

export type SubStatus = 'FREE' | 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'GRACE' | 'CANCELED' | string;

export interface SubscriptionInfo {
  tierCode: TierCode;
  subStatus: SubStatus;
  /** 가입/업그레이드 시점 동결가 — 현재 정가 아님 */
  monthlyPrice: number | null;
  billingCycle: BillingCycle | null;
  trialEndDtm: string | null;
  currentPeriodEnd: string | null;
  nextBillingDtm: string | null;
  /** 해지 예약 시점(주기말). null이면 해지 예약 없음 */
  cancelReservedDtm: string | null;
  /** 현재 티어의 동시진행현장상한 라벨(NULL도 API가 문자열로 변환해 응답) */
  maxConcurrentSiteLabel: string | null;
  /** 위 라벨의 원본 숫자값(진행률 계산용) — null이면 무제한 */
  maxConcurrentSite: number | null;
  /** 다운그레이드 예약된 다음 티어. null이면 예약 없음 */
  nextTierCode: TierCode | null;
  /** 예약된 다음 결제 주기(월간↔연간 양방향). null이면 예약 없음 — 방향 무관 항상 예약이라
   *  즉시결제는 이 값을 거치지 않는다(2026-09-10 정책 변경 — 예전엔 월간→연간만 즉시 청구였음) */
  nextBillingCycle: BillingCycle | null;
  taxAutoIssueYn: boolean;
  /** null이면 미설정 — FE가 CompanyInfo.email을 기본값으로 안내 */
  taxReceiveEmail: string | null;
  /** 유예 기간 종료 예정일 — subStatus가 GRACE일 때만 값 존재 */
  gracePeriodEndDtm: string | null;
  /** 다음 재청구(독촉) 재시도 예정일 — subStatus가 PAST_DUE일 때만 값 존재 */
  nextRetryDtm: string | null;
  /** 이 스튜디오가 실제로 결제(SUCCEEDED)를 한 번이라도 한 적 있는지 — 환불로 FREE로 돌아가도
   *  계속 true로 남는 영구 이력. C-1 STARTER·PRO CTA 문구 분기("시작하기" vs "변경하기")에 사용 */
  hasEverPaid: boolean;
  /** false면 이번 주기가 1회성 결제(카드 미등록)로 시작됐다는 뜻 — nextBillingDtm에 자동
   *  재청구되지 않고 곧바로 FREE로 전환된다(2026-09-10 신설). */
  autoRenew: boolean;
}

export interface UpdateTaxInvoiceSettingsRequest {
  autoIssueYn: boolean;
  receiveEmail: string;
}

/** CYCLE_CHANGE(같은 티어, 주기만 변경) / TIER_AND_CYCLE_CHANGE(티어·주기 동시 변경) 둘 다
 *  방향(월간↔연간) 무관하게 항상 예약(다음 결제일 반영, 즉시결제 없음) — 2026-09-10 정책 변경.
 *  즉시결제가 발생하는 건 순수 UPGRADE(주기 변경 없는 업그레이드)뿐이다(BE calcTierChange 참고). */
export type TierChangeDirection = 'UPGRADE' | 'DOWNGRADE' | 'SAME' | 'CYCLE_CHANGE' | 'TIER_AND_CYCLE_CHANGE';

export interface TierChangePreview {
  currentTierCode: TierCode;
  targetTierCode: TierCode;
  direction: TierChangeDirection;
  currentMonthlyPrice: number;
  targetMonthlyPrice: number;
  /** UPGRADE일 때만 의미 있음(즉시 결제 차액) — 티어 일할차액 + 직원 추가요금(staffSurchargeAmt)이
   *  합산된 실제 청구 예정액. DOWNGRADE/SAME은 0 */
  proratedDiff: number;
  /** proratedDiff 중 직원 추가요금분만 분리한 값(청구 시점 인원 기준, 일할계산 없이 전액) — UPGRADE가 아니면 0 */
  staffSurchargeAmt: number;
  /** UPGRADE 차액 계산에 쓰인 이번 결제주기 남은 일수 — 서버가 계산한 값 그대로 표시(클라이언트 재계산 금지) */
  remainingDays: number | null;
  currentPeriodEnd: string | null;
  targetMaxConcurrentSiteLabel: string;
  targetMaxConcurrentSite: number | null;
}

interface SitesPage {
  totalElements: number;
}

export interface StaffUsage {
  /** 소속 직원 수(STAFF_STATUS=ACTIVE) */
  staffCount: number;
  /** 현재 인원 기준 월 추가요금. 기본 제공 인원 이내면 0 */
  surchargeAmount: number;
}

export interface StaffFeeTier {
  minCnt: number | null;
  /** null이면 상한 없음(최상위 구간) */
  maxCnt: number | null;
  surchargeAmount: number;
  /** 미래 시점이면 아직 시행 전인 예정 요금표 — "정책 실시일" 안내에 사용 */
  validFromDtm: string | null;
}

/** CARD / BANK_TRANSFER / VIRTUAL_ACCOUNT — 코드값 미확정(잠정), 추후작업_TODO.md 참고 */
export type PaymentMethodType = 'CARD' | 'BANK_TRANSFER' | 'VIRTUAL_ACCOUNT' | string;

export type PaymentStatusFilter = 'ALL' | 'SUCCEEDED' | 'REFUNDED' | 'FAILED';

export interface PaymentSummary {
  /** 환불액을 뺀 실제 순액 */
  netAmount: number;
  /** 환불 제외, SUCCEEDED 건수만 */
  paymentCount: number;
  lastPaymentDtm: string | null;
  /** 결제 이력(상태 무관)이 시작된 연도 — 연도 select 옵션 범위 계산용. 이력이 없으면 null */
  firstPaymentYear: number | null;
}

export interface PaymentHistoryItem {
  paymentId: number;
  /** FAILED 건은 결제가 완료된 적이 없어 실제로는 "시도 시각"이다(BE 주석 참고) */
  paidDtm: string;
  description: string;
  paymentMethodLabel: string;
  amount: number;
  status: 'SUCCEEDED' | 'REFUNDED' | 'FAILED' | string;
  /** PG 거래키 — 영수증 승인번호 표기용. FAILED는 항상 null */
  pgTxnKey: string | null;
  /** 세금계산서 발행 상태(2026-10-15 신설) — 바로빌로 발행되는 계좌이체 결제 건(사업자번호 등록)만 값이
   *  있고, 카드 결제·사업자번호 미등록 건은 null(발행 대상 아님). 환불 행이면 수정세금계산서의 상태다. */
  taxInvoiceStatus: TaxInvoiceStatus | null;
}

/** BE G01_INVOICE_STATUS 공통코드 값 */
export type TaxInvoiceStatus = 'PENDING' | 'ISSUED' | 'SENT' | 'CANCELED' | 'FAILED' | 'NTS_FAILED';

/** GET /invoices/{paymentId}/tax-invoice 응답 */
export interface TaxInvoice {
  /** ORIGINAL(원본) / AMENDED(수정세금계산서 — 환불 결제 건, 금액이 음수) */
  invoiceType: 'ORIGINAL' | 'AMENDED';
  invoiceStatus: TaxInvoiceStatus;
  /** 작성일자(YYYY-MM-DD) */
  writeDate: string;
  supplyAmt: number;
  taxAmt: number;
  totalAmt: number;
  /** 국세청 승인번호 — 국세청 전송 완료(SENT) 후에만 값 있음 */
  ntsApprovalNo: string | null;
  /** 바로빌 세금계산서 조회 URL — 발행 완료(ISSUED·SENT)일 때만 값 있음. 발행됐어도 바로빌·연동 일시
   *  장애로 URL을 못 받으면 null(요약은 정상 응답). 유효시간이 문서화돼 있지 않아 열 때마다 새로 조회한다 */
  viewUrl: string | null;
}

export interface PaymentHistoryPage {
  items: PaymentHistoryItem[];
  hasMore: boolean;
}

export interface PaymentMethod {
  paymentMethodId: number;
  methodType: PaymentMethodType;
  /** 카드일 때만 값 있음 — 토스 발급사 코드 원본(예: "61"), 화면 표시용 아님 */
  cardIssuer: string | null;
  /** cardIssuer의 한글 카드사명(BE CardIssuerNames 매핑, 2026-09-16 신설) — 매핑에 없는 코드면
   *  null(원본 코드를 그대로 보여주지 않기 위함). 화면엔 이 값을 쓰고, null이면 "카드"로 대체 표기 */
  cardIssuerName: string | null;
  cardLast4: string | null;
  /** "개인" 또는 "법인" — 카드일 때만 값 있음(BE CARD_OWNER_TYPE, 2026-09-16 신설) */
  cardOwnerType: string | null;
  /** "신용" 또는 "체크" — 카드일 때만 값 있음(BE CARD_TYPE, 2026-09-17 신설). 이 컬럼 신설 전
   *  등록된 카드는 재등록 전까지 null(cardOwnerType과 동일한 한계) */
  cardType: string | null;
  /** 계좌이체(BANK_TRANSFER)일 때만 값 있음(2026-09-07 신설) — 토스 응답이 이미 한글이라 카드
   *  issuerCode와 달리 별도 코드→은행명 매핑 없이 그대로 표시 가능 */
  bankName: string | null;
  bankAccountLast4: string | null;
  /** 사용자 지정 별칭. 미설정이면 null */
  cardAlias: string | null;
  isDefault: boolean;
  /** PENDING / COMPLETED / FAILED — 상태값 미확정(잠정) */
  registrationStatus: string | null;
}

export interface RegisterCardRequest {
  /** 토스 SDK(requestBillingAuth) 성공 리다이렉트로 받은 1회성 인증키 */
  authKey: string;
  customerKey: string;
}

/** 정기결제용 계좌이체(퀵계좌이체) 등록 — RegisterCardRequest와 필드는 같지만 별도 타입으로 유지
 *  (2026-09-07, BE의 RegisterAccountReq와 대응 — REST 리소스명 명확화). */
export type RegisterAccountRequest = RegisterCardRequest;

export interface CompanyInfo {
  compName: string | null;
  /** 하이픈 없는 원본값 — 화면 표시 포맷팅(000-00-00000)은 FE 책임 */
  compNo: string | null;
  compRepName: string | null;
  /** 대표이메일 — 계산서 수신 이메일 입력란의 기본값 후보(수정 가능, 저장은 별개 문제) */
  compEmail: string | null;
}

export interface StartTrialRequest {
  tierCode: TierCode;
  billingCycle: BillingCycle;
  /** C-8(추천인 초대) — 추천 링크로 들어왔으면 그 추천인의 memberId(2026-09-23부로 코드 문자열이
   *  아니라 이미 해석된 값을 실어보낸다 — 코드 소유권이 sv-member로 이관되며 sv-billing은 코드
   *  자체를 더 이상 모른다. 호출부(CheckoutPage)가 billingApi.validateReferralCode로 먼저 해석). */
  referrerMemberId?: number;
}

export interface StartTrialResponse {
  subscriptionId: number;
  subStatus: SubStatus;
  trialEndDtm: string;
}

export interface CancelSubscriptionRequest {
  cancelReasonCode: string;
  cancelReasonText?: string;
}

export interface ReferralCode {
  codeValue: string;
}

/** shell-web ReferralCodeValidateResult와 동일 계약(모노레포 격리 규칙상 복제, 위 validateReferralCode 참고) */
export interface ReferralCodeValidateResult {
  valid:            boolean;
  referrerName:     string | null;
  referrerMemberId: number | null;
}

export interface ReferralInvite {
  /** "OOO 사장님" 형태로 이미 조합돼 내려옴 */
  name: string;
  joinedDtm: string;
  /** JOINED(체험중) / FIRST_PAYMENT_DONE(전환완료) */
  referralStatus: 'JOINED' | 'FIRST_PAYMENT_DONE' | string;
  earnLabel: string;
}

export interface ReferralStatus {
  convertedCount: number;
  milestoneTarget: number;
  accumulatedFreeMonths: number;
  invites: ReferralInvite[];
}

export interface RefundEligiblePayment {
  paymentId: number;
  description: string;
  paymentAmt: number;
  paidDtm: string | null;
  /** true=7일 이내(신청 가능), false=7일 경과(이 화면에서는 신청 불가 — 고객센터 문의 안내,
   *  2026-09-17 정책 — 관리자 승인 큐 제거). 서버가 요청마다 재계산한 값 — FE는 고정 문구를 쓰지
   *  않고 이 값으로 배지·문구를 분기한다(화면처리기준 §2). */
  refundEligible: boolean;
  /** 자동 즉시환불 가능 기간(7일)의 남은 일수 — 기간 지났으면 0 */
  remainingDays: number;
  /** 결제 이후 서비스 이용 내역이 있어 "자동 즉시 환불"이 거부될 건인지(사전 안내용) — 실제 차단은
   *  여전히 신청 시점에 서버가 재검증한다(활동로그 조회 실패 시 이 값은 false로 내려오므로 신청
   *  자체를 막지는 않는다). refundEligible이 false(7일 경과)인 건은 항상 false. */
  alreadyUsed: boolean;
}

export interface RefundReasonOption {
  code: string;
  label: string;
}

export interface CancelReasonOption {
  code: string;
  label: string;
}

export interface RefundRequest {
  paymentId: number;
  reasonCode: string;
  /** "기타"(ETC) 사유 선택 시에만 사용 */
  reasonText?: string;
}

export interface RefundResult {
  refundPaymentId: number | null;
  refundAmount: number;
  /** 환불과 동시에 FREE로 강등된 구독 상태 */
  subStatus: string | null;
  /** 성공 시 항상 "COMPLETED" — 처리 불가(7일 경과 등)는 응답 대신 에러로 던져진다. 운영자 승인
   *  대기(PENDING_APPROVAL) 경로는 2026-09-17 제거(사용자 결정 — 관리자 환불은 기획에 없음) */
  status: 'COMPLETED';
}

/** 사업자가 현재 구독 중인 에이전트 — sv-member(TU_AGENT_SUBSCRIPTION×TM_AGENT) 소유.
 *  agentName·agentType은 TM_AGENT에 해당 agentId가 없으면 null(추후작업_TODO.md 참고) */
export interface SubscribedAgent {
  agentId: number;
  agentName: string | null;
  agentType: string | null;
}

interface ApiResponse<T> {
  data: T;
}

/** 공통코드 1건(TC_CODE_LIST) — sv-indipia GET /indipia/codes?codeGroupCode= 응답 항목 */
export interface CodeItem {
  codeValue: string;
  codeName: string;
}

/** BE는 BILLING_CYCLE 컬럼 값을 대문자("MONTHLY"/"ANNUAL")로 그대로 내려준다(DB 컨벤션, 요청 방향은
 *  calcTierChange가 이미 대문자로 정규화해 저장하지만 응답 직렬화는 그대로 통과시킴) — FE의
 *  BillingCycle 타입은 소문자('monthly'/'annual')라 정규화 없이 비교하면(예:
 *  sub.billingCycle === 'annual') 항상 불일치한다(2026-09-07 발견 — ctaResolution의 결제 주기 변경
 *  판정, CheckoutPage 성공 폴링, SubscriptionManagePage 표시 문구가 전부 이 문제로 깨져 있었음).
 *  BE 응답 스키마는 건드리지 않고 FE 진입점(SubscriptionInfo를 응답하는 API 4종)에서 한 번만
 *  소문자로 맞춘다. */
function normalizeCycle(value: string | null): BillingCycle | null {
  return value ? (value.toLowerCase() as BillingCycle) : null;
}

function normalizeSubscription(sub: SubscriptionInfo): SubscriptionInfo {
  // 방어적 가드 — 서버는 항상 200과 함께 data.data를 채워 응답하지만(정상 흐름에서 null일 수 없음),
  // 혹시라도 응답 봉투가 비정상이면(TypeError로 새 에러를 덮어써 원래 에러의 진단 정보를 가리는 대신)
  // 그대로 통과시켜 원래 axios 에러 메시지가 살아있게 한다.
  if (!sub) return sub;
  return { ...sub, billingCycle: normalizeCycle(sub.billingCycle), nextBillingCycle: normalizeCycle(sub.nextBillingCycle) };
}

/** SubscriptionInfo를 응답하는 API 4종(getSubscription·upgradeTier·retryPayment·debugResetToFree)이
 *  전부 이 래퍼를 거치게 해 normalizeSubscription 적용을 빠뜨리지 않게 한다(2026-09-07, 4곳에
 *  각자 같은 .then() 블록을 반복하던 것을 하나로 모음) — 앞으로 SubscriptionInfo를 응답하는 API가
 *  추가되면 이 래퍼로 감싸기만 하면 된다. */
function withNormalizedSubscription<T extends { data: { data: SubscriptionInfo } }>(promise: Promise<T>): Promise<T> {
  return promise.then((res) => {
    res.data.data = normalizeSubscription(res.data.data);
    return res;
  });
}

export const billingApi = {
  /** 비로그인 허용(공개). 페이지 최초 1회만 호출 */
  getPricingPlans: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<PricingPlansResponse>>(`${API_PRX.BILLING}/pricing-plans`, { signal }),

  /** 회원 JWT 필요. 로그인 사용자에서만 호출 */
  getSubscription: (signal?: AbortSignal) =>
    withNormalizedSubscription(apiClient.get<ApiResponse<SubscriptionInfo>>(`${API_PRX.BILLING}/subscription`, { signal })),

  /** 요청 멱등성 원칙 준수를 위해 idempotencyKey 필수 — 원래 Idempotency-Key 헤더였으나 FlowGate
   *  CORS 허용 헤더 목록에 없는 커스텀 헤더라 브라우저 프리플라이트가 막혀서(2026-09-01 발견)
   *  바디 필드로 옮김. CORS는 헤더만 검사하고 바디는 안 보므로 이렇게 하면 FlowGate 수정 없이
   *  sv-billing만으로 해결된다(추후작업_TODO.md 참고) */
  startTrial: (payload: StartTrialRequest, idempotencyKey: string) =>
    apiClient.post<ApiResponse<StartTrialResponse>>(`${API_PRX.BILLING}/subscription`, {
      ...payload,
      idempotencyKey,
    }),

  /** 해지 예약(즉시 해지 아님, 주기말 예약) */
  cancelSubscription: (payload: CancelSubscriptionRequest) =>
    apiClient.delete<ApiResponse<void>>(`${API_PRX.BILLING}/subscription`, { data: payload }),

  /** 해지 예약 취소(확인 없이 즉시 실행) */
  uncancelSubscription: () =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/uncancel`),

  /** 해지 사유 선택지(C-2) — Zeus 공통코드(G01_CANCEL_REASON, 2026-09-17 전환, 환불 사유와 동일
   *  패턴) 그대로 조회. 더 이상 FE에 하드코딩한 목록을 두지 않는다. */
  getCancelReasons: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<CancelReasonOption[]>>(`${API_PRX.BILLING}/subscription/cancel-reasons`, { signal }),

  /** 진행 중 현장 수(분자) — sv-customer 소유. SiteStatus.PROGRESS만 필터링해 totalElements 사용
   *  (GET /sites/summary의 activeCount는 SiteStatus.ACTIVE=등록 기준이라 "진행중"과 다른 값이라 안 씀) */
  getSiteProgressCount: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<SitesPage>>(`${SHARED_API_PRX.CUSTOMER}/sites`, {
      params: { siteStatus: 'PROGRESS', limit: 1 },
      signal,
    }),

  /** 티어 변경 미리보기(차액·방향 계산). 실행은 아님. targetBillingCycle을 targetTierCode와 같은
   *  현재 티어로 넘기면(요금제 비교 화면의 월간↔연간 토글) 결제 주기 자체 변경 미리보기가 된다
   *  (direction='CYCLE_CHANGE'). targetTierCode가 다르고 targetBillingCycle도 다르면
   *  direction='TIER_AND_CYCLE_CHANGE'. 방향(월간↔연간) 무관하게 이 둘은 항상 예약이라 즉시결제
   *  금액은 0으로 내려온다(2026-09-10 정책 변경 — 예전엔 월간→연간만 즉시결제였음) */
  getTierChangePreview: (targetTierCode: TierCode, targetBillingCycle?: BillingCycle, signal?: AbortSignal) =>
    apiClient.get<ApiResponse<TierChangePreview>>(`${API_PRX.BILLING}/subscription/tier-change-preview`, {
      params: { targetTierCode, targetBillingCycle },
      signal,
    }),

  /** 다운그레이드 예약(결제 없음, 다음 결제일 반영 예정) */
  reserveDowngrade: (targetTierCode: TierCode) =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/downgrade`, { targetTierCode }),

  /** 다운그레이드 예약 취소(확인 없이 즉시 실행) */
  cancelDowngradeReservation: () =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/downgrade/cancel`),

  /** 결제 주기 변경 예약(월간↔연간 양방향, 결제 없음, 다음 결제일 반영 예정) — 방향 무관하게 항상
   *  예약이다(2026-09-10 정책 변경 — 예전엔 월간→연간만 upgradeTier로 즉시 청구했음). 티어도 함께
   *  바뀌면 이 API가 아니라 reserveCombinedChange를 쓴다 */
  reserveCycleChange: (targetBillingCycle: BillingCycle) =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/billing-cycle`, { targetBillingCycle }),

  /** 결제 주기 변경 예약 취소(확인 없이 즉시 실행) */
  cancelCycleChangeReservation: () =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/billing-cycle/cancel`),

  /** 티어+결제 주기 동시 변경 예약(2026-09-10 신설) — 티어 업/다운그레이드·결제 주기 방향 모두
   *  상관없이 항상 예약(결제 없음, 다음 결제일 배치에서 TIER_CODE·BILLING_CYCLE 함께 반영). 티어만
   *  바뀌면(주기 그대로) 이 API가 아니라 upgradeTier/reserveDowngrade, 주기만 바뀌면(티어 그대로)
   *  reserveCycleChange를 쓴다 — "둘 다" 바뀔 때만 이 API. */
  reserveCombinedChange: (targetTierCode: TierCode, targetBillingCycle: BillingCycle) =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/combined-change`, { targetTierCode, targetBillingCycle }),

  /** 티어+결제 주기 동시 변경 예약 취소(확인 없이 즉시 실행) */
  cancelCombinedChangeReservation: () =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/combined-change/cancel`),

  /** 업그레이드 즉시 결제 — 주기 변경 없는 순수 업그레이드 전용(2026-09-10 정책 변경 — 예전엔
   *  결제 주기 변경도 이 API로 즉시 청구했으나, 이제 주기가 함께 바뀌면 방향 무관 항상 예약이라
   *  reserveCycleChange/reserveCombinedChange를 대신 쓴다. 이미 ACTIVE·non-FREE인 구독에 기존과
   *  다른 주기를 넘기면 서버가 "업그레이드 대상 티어가 아닙니다"로 거부함). targetBillingCycle은
   *  그 외의 경우 — FREE·체험 등 기존 결제 주기가 없거나 의미 없는 첫 유료 전환에서, 요금제 비교
   *  화면의 주기 토글 선택을 실제 첫 결제에 반영하기 위해 넘긴다(2026-09-15 발견·수정 — 예전엔 이
   *  값 자체를 안 보내서 연간을 골라도 첫 결제가 항상 월간으로 나갔음). 차액은 클라이언트가 안
   *  보냄(서버가 재계산). idempotencyKey는 헤더가 아니라 바디 필드로 보낸다(FlowGate CORS 미허용
   *  헤더 회피, startTrial과 동일 이유). agreedAutoRenewal — 체크아웃 화면의 "정기 결제 및 자동
   *  갱신에 동의합니다(필수)" 체크박스 값. 서버가 false면 거부하므로(위변조 방지) 버튼이 이미
   *  비활성화돼 있어도 항상 실제 체크값을 보낸다. paymentMethodId — 결제 확인 화면에서 사용자가
   *  고른 카드(2026-09-18 정책 확정: "자동결제는 기본카드로, 지금 이 결제는 사용자가 선택한
   *  카드로"). 넘기면 그 카드로만 이번 건이 청구되고 기본카드는 안 바뀐다 — 안 넘기면(undefined)
   *  서버가 그 순간의 기본카드로 대체 처리한다(하위 호환). */
  upgradeTier: (
    targetTierCode: TierCode,
    idempotencyKey: string,
    agreedAutoRenewal: boolean,
    targetBillingCycle?: BillingCycle,
    paymentMethodId?: number,
  ) =>
    withNormalizedSubscription(apiClient.post<ApiResponse<SubscriptionInfo>>(
      `${API_PRX.BILLING}/subscription/upgrade`,
      { targetTierCode, idempotencyKey, agreedAutoRenewal, targetBillingCycle, paymentMethodId },
      { timeout: PG_CALL_TIMEOUT_MS },
    )),

  /** 1회성 결제(결제수단 미등록, 2026-09-10 신설) — 체크아웃의 "1회만 결제" 흐름 전용. 토스 SDK
   *  requestPayment()(빌링키 미사용 일반 결제창)가 이미 승인까지 끝낸 뒤 돌려준 paymentKey를 여기서
   *  최종 승인한다. amount는 안 보낸다(UpgradeTierReq와 동일 원칙, 2026-09-10 리뷰에서 정리 —
   *  서버가 calcTierChange로 재계산한 값만 신뢰한다). 카드를 저장하지 않으므로 이 결제로 전환된
   *  티어는 다음 결제일에 자동 청구되지 않고 곧바로 FREE로 전환된다(정책 확정, 2026-09-10).
   *  idempotencyKey는 다른 결제류 API와 동일하게 바디 필드. */
  confirmOneTimePayment: (payload: { targetTierCode: TierCode; paymentKey: string; orderId: string }, idempotencyKey: string) =>
    withNormalizedSubscription(apiClient.post<ApiResponse<SubscriptionInfo>>(
      `${API_PRX.BILLING}/subscription/one-time-payment`,
      { ...payload, idempotencyKey },
      { timeout: PG_CALL_TIMEOUT_MS },
    )),

  /** 결제 재시도(수동) — PAST_DUE·GRACE 상태에서 예약된 재시도일을 기다리지 않고 지금 바로
   *  재청구를 시도한다. 실패해도 200으로 응답 — 호출부가 응답의 subStatus로 성공 여부를 판단한다.
   *  idempotencyKey는 헤더가 아니라 바디 필드로 보낸다(FlowGate CORS 미허용 헤더 회피) */
  retryPayment: (idempotencyKey: string) =>
    withNormalizedSubscription(apiClient.post<ApiResponse<SubscriptionInfo>>(
      `${API_PRX.BILLING}/subscription/retry-payment`,
      { idempotencyKey },
      { timeout: PG_CALL_TIMEOUT_MS },
    )),

  /** 세금계산서 자동발행 설정 저장(TU_SUBSCRIPTION 컬럼, 결제 여부와 무관하게 즉시 반영) */
  updateTaxInvoiceSettings: (payload: UpdateTaxInvoiceSettingsRequest) =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/tax-invoice-settings`, payload),

  /** 기본 결제수단이 먼저 오도록 정렬돼 응답 */
  getPaymentMethods: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<PaymentMethod[]>>(`${API_PRX.BILLING}/payment-methods`, { signal }),

  /** 정기결제가 필요한 상태에서 마지막 결제수단이면 서버가 409로 차단. 기본 결제수단도 차단(먼저 다른 카드를 기본으로 설정) */
  deletePaymentMethod: (paymentMethodId: number) =>
    apiClient.delete<ApiResponse<void>>(`${API_PRX.BILLING}/payment-methods/${paymentMethodId}`),

  /** 카드 별칭 설정·수정. 빈 문자열이면 별칭 해제 */
  updateCardAlias: (paymentMethodId: number, cardAlias: string) =>
    apiClient.patch<ApiResponse<PaymentMethod>>(`${API_PRX.BILLING}/payment-methods/${paymentMethodId}/alias`, { cardAlias }),

  /** 기존 기본 결제수단은 서버가 자동 해제(항상 기본 1개만 유지) */
  setDefaultPaymentMethod: (paymentMethodId: number) =>
    apiClient.patch<ApiResponse<void>>(`${API_PRX.BILLING}/payment-methods/${paymentMethodId}/default`),

  /** 세금계산서 섹션 사업자정보(읽기전용) — 회원가입 시 등록값을 sv-member에서 그대로 조회 */
  getCompanyInfo: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<CompanyInfo>>(`${API_PRX.BILLING}/company-info`, { signal }),

  /** 직원 계정 사용량 — "지금 이 순간" 기준 소속 직원 수·추가요금(청구용 동결가 아님) */
  getStaffUsage: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<StaffUsage>>(`${API_PRX.BILLING}/subscription/staff-usage`, { signal }),

  /** 비로그인 허용(공개) — 스튜디오별 데이터가 아니라 전체 공통 정책 */
  getStaffFeeTiers: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<StaffFeeTier[]>>(`${API_PRX.BILLING}/subscription/staff-fee-tiers`, { signal }),

  /** 누적 결제 금액(환불 차감 순액)·결제 건수·마지막 결제일 */
  getPaymentSummary: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<PaymentSummary>>(`${API_PRX.BILLING}/invoices/summary`, { signal }),

  /** 무한스크롤 페이징(offset/limit) — 센티넬이 뷰포트에 들어올 때마다 offset을 10씩 늘려 재호출 */
  getPaymentHistory: (
    params: { year: number; status: PaymentStatusFilter; offset: number; limit: number },
    signal?: AbortSignal,
  ) => apiClient.get<ApiResponse<PaymentHistoryPage>>(`${API_PRX.BILLING}/invoices`, { params, signal }),

  /** 결제 1건의 세금계산서(바로빌) — 상태·금액·국세청 승인번호와 발행 후 조회 URL(2026-10-15 신설).
   *  5초 타임아웃(D-07 §3.2 #11 — 5초 이상 로딩 금지): BE는 조회 URL을 4초 안에 못 받으면 URL 없이
   *  요약만 응답하므로 정상이면 그 안에 끝나고, 넘기면 모달이 ErrorState로 전환된다. */
  getTaxInvoice: (paymentId: number, signal?: AbortSignal) =>
    apiClient.get<ApiResponse<TaxInvoice>>(`${API_PRX.BILLING}/invoices/${paymentId}/tax-invoice`, {
      signal,
      timeout: 5000,
    }),

  /** authKey로 서버가 토스 API를 호출해 실제 빌링키 발급을 요청 — 카드 유효성은 이 단계에서 PG가 판정 */
  registerCard: (payload: RegisterCardRequest) =>
    apiClient.post<ApiResponse<PaymentMethod>>(`${API_PRX.BILLING}/payment-methods/card`, payload, {
      timeout: PG_CALL_TIMEOUT_MS,
    }),

  /** 정기결제용 계좌이체(퀵계좌이체) 등록(2026-09-07 신설) — registerCard와 동일 패턴 */
  registerAccount: (payload: RegisterAccountRequest) =>
    apiClient.post<ApiResponse<PaymentMethod>>(`${API_PRX.BILLING}/payment-methods/account`, payload, {
      timeout: PG_CALL_TIMEOUT_MS,
    }),

  /** 내 추천 코드 — 2026-09-23부로 sv-member 소유(TU_MEMBER.REFERRAL_CODE로 이관, MASTER_YN='Y'
   *  행에만 존재). 없으면 서버가 즉시 발급. */
  getMyReferralCode: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<ReferralCode>>(`${SHARED_API_PRX.MEMBER}/referrals/my-code`, { signal }),

  /** 추천 코드 존재 확인(공개, 비로그인 허용) — 로그인 상태에서 `?ref=` 링크로 들어온 방문자가
   *  startTrial 호출 전에 코드를 referrerMemberId로 해석하는 데 쓴다(CheckoutPage 참고). 코드
   *  소유권이 sv-member로 이관되며 신설(2026-09-23) — apps/shell-web의
   *  shared/api/referral.ts#validateReferralCode와 동일 계약이지만, 에이전트 패키지는 shell-web을
   *  import할 수 없어(모노레포 격리 규칙) 그대로 복제해 둔다. */
  validateReferralCode: (code: string, signal?: AbortSignal) =>
    apiClient.get<ApiResponse<ReferralCodeValidateResult>>(`${SHARED_API_PRX.MEMBER}/referrals/validate`, {
      params: { code },
      signal,
    }),

  /** 추천 현황판(C-8-5) — 진행률·초대 목록·누적 무료개월 */
  getReferralStatus: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<ReferralStatus>>(`${API_PRX.BILLING}/referrals/status`, { signal }),

  /** 추천 링크 재발송(확인용, C-8-3 보조) — channel(KAKAO/SMS)은 실제 채널을 강제하지 않고 BE가
   *  Hermes 정책의 채널별 조건 매칭 훅으로 넘길 뿐이다(2026-09-16, ReferralService 참고) — 정책에
   *  해당 조건이 등록돼 있어야 클릭한 버튼과 실제 발송 채널이 일치한다. */
  resendReferralLink: (channel: 'KAKAO' | 'SMS') =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/referrals/resend`, null, { params: { channel } }),

  /** 환불 신청(C-10) 대상 결제건 목록 — 결제 후 7일 이내 건만 신청 가능(서버가 매번 재계산) */
  getRefundEligiblePayments: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<RefundEligiblePayment[]>>(`${API_PRX.BILLING}/refund-eligible-payments`, { signal }),

  /** 환불 사유 선택지(C-10-4) — Zeus 공통코드(G01_REFUND_REASON, 2026-09-17 전환) 그대로 조회.
   *  더 이상 FE에 하드코딩한 목록을 두지 않는다. */
  getRefundReasons: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<RefundReasonOption[]>>(`${API_PRX.BILLING}/refund-reasons`, { signal }),

  /** 환불 신청 — 7일 이내는 사용여부와 무관하게 전액환불. 승인과 동시에 구독은 FREE로 강등됨 */
  requestRefund: (payload: RefundRequest, idempotencyKey: string) =>
    apiClient.post<ApiResponse<RefundResult>>(
      `${API_PRX.BILLING}/refunds`,
      { ...payload, idempotencyKey },
      { timeout: PG_CALL_TIMEOUT_MS },
    ),

  /** 포함된 에이전트(C-2-8) — sv-member 소유. compId는 FlowGate가 JWT claim으로 주입하므로
   *  FE가 헤더로 직접 보내지 않는다(getSiteProgressCount와 동일 패턴) */
  getSubscribedAgents: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<SubscribedAgent[]>>(`${SHARED_API_PRX.MEMBER}/studio/agents`, { signal }),

  /** 결제상태 라벨(TC_CODE_LIST, 코드그룹 G01_PAYMENT_STATUS) — sv-indipia 공통코드 조회.
   *  scheduler 에이전트의 CommonCodesContext(E01_SCHEDULE_STATUS)와 동일 관례: 조회 실패해도
   *  호출부가 하드코딩 라벨로 폴백하므로 이 호출 실패가 화면을 막지 않는다. */
  getPaymentStatusCodes: (signal?: AbortSignal) =>
    apiClient.get<ApiResponse<CodeItem[]>>(`${SHARED_API_PRX.INDIPIA}/codes`, {
      params: { codeGroupCode: 'G01_PAYMENT_STATUS' },
      signal,
    }),

  /** [개발전용] 테스트 데이터를 빠르게 FREE로 되돌리기 위한 임시 버튼용 — BE
   *  SubscriptionDebugController(prod 프로파일에서 라우트 자체가 없음, 2026-09-03)를 그대로 호출.
   *  실서비스 배포 전 버튼·이 함수 모두 제거 대상(추후작업_TODO.md 참고) */
  debugResetToFree: () =>
    withNormalizedSubscription(apiClient.post<ApiResponse<SubscriptionInfo>>(`${API_PRX.BILLING}/subscription/dev/reset-free`)),

  /** [개발전용] 알림톡 발송 파이프라인(Outbox→RabbitMQ→Hermes) 7종 즉시 발송 테스트용 — 구독결제_
   *  알림톡_알림유형_정의.md 7개 시나리오와 1:1 대응. BE SubscriptionDebugController(prod 프로파일 미노출)
   *  test-notification/* 를 그대로 호출. 실서비스 배포 전 버튼·이 함수들 모두 제거 대상. */
  testNotificationTrialEnding: () =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/dev/test-notification/trial-ending`),
  testNotificationPaymentUpcoming: () =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/dev/test-notification/payment-upcoming`),
  testNotificationPaymentSucceeded: () =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/dev/test-notification/payment-succeeded`),
  testNotificationPaymentFailed: () =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/dev/test-notification/payment-failed`),
  testNotificationPaymentMethodChanged: () =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/dev/test-notification/payment-method-changed`),
  testNotificationReferralLinkIssued: () =>
    apiClient.post<ApiResponse<void>>(`${API_PRX.BILLING}/subscription/dev/test-notification/referral-link-issued`),
};
